import { MembershipType, Sangha } from "@app/shared/types";
import { Button, Link, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { externalImageSrc } from "app/images";
import { titleFont } from "app/theme";
import { TeacherDialog } from "features/community/TeacherDialog";
import { SanghaDayAndTimeDescription } from "features/sangha/SanghaDayAndTimeDescription";
import { useUserTimezone } from "hooks/useUserTimezone";
import { DateTime } from "luxon";
import { useState } from "react";
import EventCard from "components/EventCard";
import { SpotsAvailableButton } from "./SpotsAvailableButton";
import { PRIMARY_ADMIN_ACCOUNT_NAME } from "@app/shared/constants";
import { NavLink } from "features/navigation/NavLink";

const useStyles = makeStyles((theme) => ({
    card: {},
    teacherLink: {
        color: theme.palette.grey700,
    },
    immersionPromotionLink: {
        color: theme.palette.grey700,
    },
    eventName: {
        marginBottom: theme.spacing(1),
    },
    eventTime: {},
    col2: {
        display: "inline-block",
        [theme.breakpoints.down("md")]: {
            display: "block",
        },
    },
    description: {
        display: "inline-block",
        marginRight: theme.spacing(2),
        marginBottom: 0,
        [theme.breakpoints.down("lg")]: {
            marginBottom: theme.spacing(2),
        },
    },
    messageWrapper: {
        textAlign: "right",
        [theme.breakpoints.down("md")]: {
            textAlign: "center",
        },
    },
    eventTimeDisplay: {
        marginRight: theme.spacing(2),
        marginBottom: theme.spacing(1),
        display: "inline-block",
        [theme.breakpoints.down("md")]: {
            display: "block",
            marginRight: 0,
        },
    },
    actionBar: {
        // display: "block",
    },
    spotsAvailableButton: {},
    claimYourSpotButton: {
        whiteSpace: "nowrap",
        [theme.breakpoints.down("sm")]: {
            width: "100%",
        },
    },
    groupStatus: {},
    cardContent: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-end",
        [theme.breakpoints.down("lg")]: {
            flexDirection: "column",
            alignItems: "flex-start",
        },
    },
    mainContent: {},
    buttonContainer: {
        display: "flex",
        flexDirection: "row",
        gap: theme.spacing(2),
        [theme.breakpoints.down("lg")]: {
            flexWrap: "wrap",
        },
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            width: "100%",
        },
    },
}));

export const ImmersionGroupCard = (props: {
    sangha: Sangha;
    nextSessionStartTime?: DateTime;
    nextSessionEndTime?: DateTime;
    ref?: React.Ref<HTMLDivElement>;
    handleOnClaimSpot?: () => void;
    claimButtonText?: React.ReactNode;
    hideLearnMore?: boolean;
    hideSpotsAvailable?: boolean;
    extraButton?: React.ReactNode;
    isAddOnDisabled?: boolean;
}) => {
    const classes = useStyles();

    const {
        sangha,
        handleOnClaimSpot,
        ref,
        claimButtonText,
        hideLearnMore,
        hideSpotsAvailable,
        isAddOnDisabled,
    } = props;

    const timezone = useUserTimezone();

    const teacher = sangha.teacher;

    const [teacherDialogOpen, setTeacherDialogOpen] = useState(false);

    const nextSessionStartTime =
        props.nextSessionStartTime ||
        (sangha.schedule.nextSession && DateTime.fromISO(sangha.schedule.nextSession.time));
    const nextSessionEndTime =
        props.nextSessionEndTime ||
        (sangha.schedule.nextSession && DateTime.fromISO(sangha.schedule.nextSession.endTime));

    if (!nextSessionStartTime || !nextSessionEndTime) {
        return null;
    }

    const renderSpotsAvailable = () => {
        return (
            <SpotsAvailableButton
                sangha={sangha}
                classes={{ button: classes.spotsAvailableButton }}
            />
        );
    };

    const renderButtons = () => (
        <div className={classes.buttonContainer}>
            {props.extraButton}
            {sangha.memberships && !hideSpotsAvailable && renderSpotsAvailable()}
            {claimButtonText && (
                <Button
                    className={classes.claimYourSpotButton}
                    variant="primary"
                    size="small"
                    onClick={handleOnClaimSpot}
                    disabled={isAddOnDisabled}
                >
                    {claimButtonText}
                </Button>
            )}
        </div>
    );

    const getAdditionalInfo = () => {
        if (sangha.eligibleMembershipTypes[0] === MembershipType.mmtcp) {
            return "For Meditation Teachers";
        }
        if (sangha.audienceDisplay && sangha.audienceDisplay !== "all meditators") {
            switch (sangha.audienceDisplay) {
                case "women":
                    return "For Women";
                case "people of color":
                    return "For People of Color";
                case "60+ year olds":
                    return "For 60+ year olds";
                case "LGBTQIA+":
                    return "For LGBTQIA+";
                case "experienced":
                    return "For Experienced Meditators";
                case "psychotherapists":
                    return "For Psychotherapists";
                case "mindful leadership":
                    return "For Mindful Leaders";
                default:
                    return `For ${sangha.audienceDisplay}`;
            }
        }
    };

    const getTitle = () => {
        const additionalInfo = getAdditionalInfo();

        return (
            <>
                <div>
                    <Typography variant="h4" className={classes.eventName}>
                        {teacher.name === PRIMARY_ADMIN_ACCOUNT_NAME ? (
                            "Mystery Teacher"
                        ) : (
                            <Button
                                variant="tertiary"
                                className={classes.teacherLink}
                                onClick={() => setTeacherDialogOpen(true)}
                            >
                                {`${teacher.name}'s`}
                            </Button>
                        )}{" "}
                        Mentorship Group
                        {additionalInfo && ` ${additionalInfo}`}
                    </Typography>
                </div>
                <TeacherDialog
                    open={teacherDialogOpen}
                    onClose={() => setTeacherDialogOpen(false)}
                    teacher={teacher}
                />
            </>
        );
    };

    return (
        <>
            <EventCard
                containerAttributes={{
                    ref: ref,
                    "data-testid": "immersionGroupCard",
                    "data-groupid": sangha.id,
                }}
                pictures={[teacher.picture ? externalImageSrc(teacher.picture, 260) : "EMPTY"]}
                pictureOnClick={() => setTeacherDialogOpen(true)}
                information={
                    <div className={classes.cardContent}>
                        <div className={classes.mainContent}>
                            <Typography variant="caption" className={classes.groupStatus}>
                                <strong>Open</strong>
                            </Typography>
                            <Typography variant="h3" className={classes.eventName}>
                                {getTitle()}
                            </Typography>
                            <div className={classes.eventTime}>
                                <Typography
                                    variant="body1"
                                    className={classes.eventTimeDisplay}
                                    data-testid="eventTime"
                                >
                                    <SanghaDayAndTimeDescription
                                        sangha={sangha}
                                        userTimeZone={timezone}
                                        renderTimeZone={true}
                                        overrideStartTime={nextSessionStartTime}
                                        overrideEndTime={nextSessionEndTime}
                                    />
                                </Typography>
                            </div>
                            {!hideLearnMore && (
                                <Typography variant="caption" className={classes.description}>
                                    Meet with the same group and teacher each week on your shared
                                    journey.{" "}
                                    <NavLink
                                        className={classes.immersionPromotionLink}
                                        onClick={handleOnClaimSpot}
                                    >
                                        Learn more
                                    </NavLink>
                                </Typography>
                            )}
                        </div>
                        {renderButtons()}
                    </div>
                }
            />
        </>
    );
};

export default ImmersionGroupCard;
